import { ListWrapper, TableCell } from 'components/Hospital/PaginationForms';
import { clManagingPatientsFavoriteListWidth, clManagingPatientsListWidth } from 'constants/pagination';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { pagePaths } from 'constants/path';
import { changeMobileForm } from 'utils/changeMobileForm';
import ArrowLineRoundRight from 'assets/Icons/Arrow/ArrowLineRoundRight';
import GenderMan from 'assets/Icons/Gender/GenderMan';
import GenderWoman from 'assets/Icons/Gender/GenderWoman';
import { ICLUserInfo } from 'models/CL/CLBasicInterface';
import SelectBlueCheckBox from 'assets/Icons/CheckBox/SelectBlueCheckBox';
import styled from 'styled-components';
import { BackgroundColor, BorderColor, IconColor, PrimaryColor } from 'styles/Global/colorSemantic';
import useUpdateFavoriteUserMutation from 'api/Mutations/CL/useUpdateFavoriteUserMutation';

// TableCell의 onClick 타입이 맞지 않을 경우, TableCell 컴포넌트의 props에 onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void; 를 추가해야 합니다.

export type CLManagingPatientsPaginationTableProps = {
    data: ICLUserInfo[];
    selectedPatients?: string[];
    onPatientSelect?: (patientId: string) => void;
    showFavoriteButton?: boolean;
    refetchUserListData?: () => void;
    refetchFavoriteUserListData?: () => void;
};

const CLManagingPatientsPaginationTable = ({
    data,
    selectedPatients = [],
    onPatientSelect,
    showFavoriteButton = false,
    refetchUserListData,
    refetchFavoriteUserListData,
}: CLManagingPatientsPaginationTableProps) => {
    const navigate = useNavigate();
    const updateFavoriteUserMutation = useUpdateFavoriteUserMutation();
    const tableWidths = showFavoriteButton ? clManagingPatientsFavoriteListWidth : clManagingPatientsListWidth;

    const handleCheckboxClick = async (e: React.MouseEvent, patientId: string, item: ICLUserInfo) => {
        e.stopPropagation();

        if (showFavoriteButton) {
            try {
                await updateFavoriteUserMutation.mutateAsync({
                    userId: item._id || '',
                    isFavorite: !item.isFavorite,
                });
                refetchUserListData?.();
                refetchFavoriteUserListData?.();
            } catch (error) {
                console.error('즐겨찾기 업데이트 실패:', error);
            }
        }

        onPatientSelect?.(patientId);
    };

    const handleRowClick = (item: ICLUserInfo) => {
        if (!item._id) return;
        navigate(`${pagePaths.CLAdminPatientInfoBase}/${item._id}`, { state: item });
    };

    return (
        <>
            {data.map((item, idx) => {
                const patientKey = `${item._id || item.uid}-${idx}`;
                const isSelected = selectedPatients.includes(patientKey);
                return (
                    <ListWrapper key={patientKey} onClick={() => handleRowClick(item)} $borderBottom='visible'>
                        {showFavoriteButton && (
                            <TableCell $width={tableWidths[0]} $type='todayDiagnosis' $justifyContent='center' onClick={(e: React.MouseEvent) => handleCheckboxClick(e, patientKey, item)}>
                                <CheckBoxWrapper>{item.isFavorite || isSelected ? <SelectBlueCheckBox /> : <CheckBox />}</CheckBoxWrapper>
                            </TableCell>
                        )}
                        <TableCell $width={showFavoriteButton ? tableWidths[1] : tableWidths[0]} $type='name'>
                            {item.name + ' '}
                            {item.gender === 'male' && <GenderMan />}
                            {item.gender === 'female' && <GenderWoman />}
                        </TableCell>
                        <TableCell $width={showFavoriteButton ? tableWidths[2] : tableWidths[1]}>{item.birthday ? moment(item.birthday, 'YYYYMMDD').format('YYYY/MM/DD') : '미입력'}</TableCell>
                        <TableCell $width={showFavoriteButton ? tableWidths[3] : tableWidths[2]}>{changeMobileForm(item.mobile || '미입력')}</TableCell>
                        <TableCell $width={showFavoriteButton ? tableWidths[4] : tableWidths[3]}>{moment(item.registrationTimestamp).format('YYYY/MM/DD')}</TableCell>
                        <TableCell $width={showFavoriteButton ? tableWidths[5] : tableWidths[4]} $maxWidth='60px'>
                            <ArrowLineRoundRight />
                        </TableCell>
                    </ListWrapper>
                );
            })}
        </>
    );
};

export default CLManagingPatientsPaginationTable;

const CheckBox = styled.div<{ disabled?: boolean }>`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    border: 1px solid ${IconColor.color_icon_light};
    background-color: ${BackgroundColor.color_background_white};
    &:hover {
        border: 1px solid ${IconColor.color_icon_neutral};
    }
    &:active {
        border: 1px solid ${PrimaryColor.color_primary_normal};
    }
    &:focus {
        border: 1px solid ${PrimaryColor.color_primary_normal};
    }
    ${(props) =>
        props.disabled &&
        `
        border: 1px solid ${BorderColor.color_border_neutral};
        background-color: ${BackgroundColor.color_background_disabled};
    `}
`;

const CheckBoxWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
`;
